import { useState, type FormEvent } from 'react';
import { useNavigate, useParams, useSearchParams, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@contexts/useAuth';
import { GenbaNotPrimeUserError, GenbaRootUrlError, InvalidCredentialsError, type AuthUser } from '@apis/auth';
import { PROVIDER_NAMES, type ProviderName } from '@interfaces/IAuth';
import { Field } from '@components/ui/Field/Field';
import { Button } from '@components/ui/Button/Button';
import styles from './styles.module.css';

type ProviderConfig = {
  /** Genba encodes the tenant in its API root URL; Trusstor needs no such field. */
  needsRootUrl: boolean;
  /** Reads this provider's bound login id off the user record (captured
      server-side at last connect) so a reconnect prefills the login field. */
  storedLoginId: (user: Pick<AuthUser, 'trusstorLoginId' | 'genbaLoginId'> | null) => string | undefined;
  logo?: string;
};

// Display copy (name / blurb / field labels) is localized via i18n
// (`auth.connect.provider.<provider>.*`); this map holds only the structural,
// non-localized bits of each provider's connect flow.
const PROVIDER_CONFIG: Record<ProviderName, ProviderConfig> = {
  trusstor: {
    needsRootUrl: false,
    storedLoginId: (user) => user?.trusstorLoginId,
    logo: '/trusstor-logo.svg',
  },
  genba: {
    needsRootUrl: true,
    storedLoginId: (user) => user?.genbaLoginId,
  },
};

function isProvider(x: string | undefined): x is ProviderName {
  return PROVIDER_NAMES.includes(x as ProviderName);
}

/** The provider's brand mark — Genba's 現場Plus lockup, or the provider logo. */
function ProviderBrand({ provider, config, name }: { provider: ProviderName; config: ProviderConfig; name: string }) {
  if (provider === 'genba') {
    return (
      <span className={styles.genbaLockup}>
        <img src="/genba-mark.png" alt="" className={styles.genbaMark} />
        <span className={styles.genbaWordmark}>
          現場<span className={styles.genbaPlus}>Plus</span>
        </span>
      </span>
    );
  }
  return config.logo ? <img src={config.logo} alt={name} className={styles.providerLogo} /> : null;
}

export function ConnectProviderPage() {
  const { t } = useTranslation();
  const { provider } = useParams();
  const { connectProvider, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Return to wherever the reconnect prompt fired from (set by the caller, e.g.
  // the TopBar reconnect button / project-merge "Reconnect" button); the
  // dashboard is the fallback for a direct visit. react-router's navigate()
  // treats a string as an in-app path, so an off-site value can't escape here.
  const redirectTo = searchParams.get('redirect') ?? '/';
  // The user's bound Genba root URL (persisted server-side at last connect,
  // returned on /login and /me) prefills the field; a first-ever connect starts
  // blank. Kept server-side only — no web-storage mirror of upstream identity.
  const [rootUrl, setRootUrl] = useState(user?.genbaRootUrl ?? '');
  // Prefill the login field from the identity bound to the user at last connect
  // (server-side, both providers), so a reconnect needs only the password.
  // `provider` isn't validated until after the hooks run, so guard the lookup.
  const [username, setUsername] = useState(
    () => (isProvider(provider) ? PROVIDER_CONFIG[provider].storedLoginId(user) : undefined) ?? '',
  );
  const [password, setPassword] = useState('');
  const [editingRootUrl, setEditingRootUrl] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (!isProvider(provider)) return <Navigate to="/" replace />;

  // `provider` is narrowed to ProviderName by the guard above; capture it as a
  // typed const so the onSubmit closure keeps the narrowing (a closure re-widens
  // the raw useParams value back to `string | undefined`).
  const providerName = provider;
  const config = PROVIDER_CONFIG[providerName];
  const { needsRootUrl } = config;
  const name = t(`auth.connect.provider.${providerName}.name`);
  const blurb = t(`auth.connect.provider.${providerName}.blurb`);
  const usernameLabel = t(`auth.connect.provider.${providerName}.usernameLabel`);
  // Once a user has a bound Genba root URL, the long field collapses to a
  // read-only summary with an "Edit" affordance — a reconnect is then just
  // login ID + password. First-ever connect (no stored URL) shows the field.
  const hasStoredRootUrl = needsRootUrl && Boolean(user?.genbaRootUrl);
  const showRootUrlField = needsRootUrl && (!hasStoredRootUrl || editingRootUrl);
  // With the login field prefilled from the user record, the only thing left to
  // type is the password — land focus there. Fall back to the login field when
  // it's empty (a first connect with nothing stored).
  const focusPassword = !showRootUrlField && username.trim().length > 0;
  const focusUsername = !showRootUrlField && !focusPassword;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setSubmitting(true);
    try {
      await connectProvider(providerName, username, password, needsRootUrl ? { rootUrl } : undefined);
      navigate(redirectTo, { replace: true });
    } catch (error) {
      if (error instanceof InvalidCredentialsError) {
        setErr(t('auth.connect.error.invalidCredentials', { provider: name }));
      } else if (error instanceof GenbaNotPrimeUserError) {
        setErr(t('auth.connect.error.notPrimeUser'));
      } else if (error instanceof GenbaRootUrlError) {
        setErr(error.message || t('auth.connect.error.rootUrl'));
      } else {
        setErr(t('auth.connect.error.generic', { provider: name }));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.providerBrand}>
          <ProviderBrand provider={providerName} config={config} name={name} />
        </div>
        <h1 className={styles.title}>{t('auth.connect.title', { provider: name })}</h1>
        <p className={styles.subtitle}>{blurb}</p>
        <p className={styles.note}>{t('auth.connect.note', { provider: name })}</p>
        <form onSubmit={onSubmit} className={styles.form}>
          {showRootUrlField ? (
            <Field
              label={t('auth.connect.genbaRootUrlLabel')}
              type="url"
              value={rootUrl}
              onChange={(e) => setRootUrl(e.target.value)}
              placeholder="https://apg.kensetsu-cloud.jp/jp/XXXXXX/api"
              hint={t('auth.connect.genbaRootUrlHint')}
              autoComplete="off"
              autoFocus
              required
            />
          ) : null}
          <Field
            label={usernameLabel}
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete={needsRootUrl ? 'email' : 'username'}
            autoFocus={focusUsername}
            required
          />
          <Field
            label={t('auth.connect.passwordLabel', { provider: name })}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            autoFocus={focusPassword}
            required
          />
          {err ? <div className={styles.error} role="alert">{err}</div> : null}
          <div className={styles.actions}>
            {hasStoredRootUrl && !editingRootUrl ? (
              <button
                type="button"
                className={styles.editRootUrlLink}
                onClick={() => setEditingRootUrl(true)}
              >
                {t('auth.connect.editGenbaUrl')}
              </button>
            ) : null}
            <div className={styles.actionButtons}>
              <Button variant="ghost" onClick={() => navigate(-1)}>
                {t('auth.connect.cancel')}
              </Button>
              <Button type="submit" variant="primary" disabled={submitting}>
                {submitting ? t('auth.connect.connecting') : t('auth.connect.submit', { provider: name })}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
